import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { CreditCard, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Small dialog to override a bank-synced card's credit_limit and due_date.
 * Plaid / Open Banking rarely exposes these fields on the raw account payload,
 * so the user fills them in manually. Saving unlocks the Optimizer + Simulator.
 */
const SaltEdgeOverrideDialog = ({ open, onOpenChange, card, onSaved }) => {
  const [limit, setLimit] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [nickname, setNickname] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLimit(card?.credit_limit != null ? String(card.credit_limit) : "");
    setDueDate(card?.due_date || "");
    setNickname(card?.nickname || "");
  }, [open, card]);

  const submit = async (e) => {
    e.preventDefault();
    if (!card?.id) return;
    const limNum = limit === "" ? null : Number(limit);
    if (limNum !== null && (!Number.isFinite(limNum) || limNum < 0)) {
      toast.error("Credit limit must be zero or greater");
      return;
    }
    if (card.balance != null && limNum !== null && limNum > 0 && card.balance > limNum) {
      toast.error("Current balance is higher than the limit — double-check the numbers");
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API}/saltedge/accounts/${card.id}/override`,
        {
          credit_limit: limNum,
          due_date: dueDate || null,
          nickname: nickname.trim() || null,
        },
        { withCredentials: true }
      );
      toast.success(`Updated ${nickname.trim() || card.name}`);
      onOpenChange(false);
      onSaved && onSaved();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : "Could not save. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0E172C] border border-[#1A2849] text-white sm:max-w-md rounded-2xl p-4 sm:p-6"
        data-testid="saltedge-override-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-2.5 sm:gap-3">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[#00E676]/10 border border-[#00E676]/30 flex items-center justify-center flex-shrink-0">
              <CreditCard size={15} className="text-[#00E676]" />
            </div>
            <div className="min-w-0">
              <DialogTitle
                className="text-white text-base sm:text-xl tracking-tight"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Edit {card?.nickname || card?.name || "linked card"}
              </DialogTitle>
              <DialogDescription className="text-slate-300 text-xs sm:text-sm">
                Your bank doesn't share these — enter them to unlock utilization,
                the Optimizer, and reminders.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={submit} className="mt-3 space-y-3">
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] text-slate-300 font-semibold">
              Nickname (optional)
            </label>
            <input
              type="text"
              value={nickname}
              onChange={(e) => setNickname(e.target.value.slice(0, 30))}
              maxLength={30}
              placeholder={`e.g. Amex Everyday (${nickname.length}/30)`}
              data-testid="saltedge-override-nickname"
              className="mt-1.5 w-full rounded-lg bg-[#060B19] border border-[#1A2849] px-3 sm:px-3.5 py-2 sm:py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#00E676] focus:ring-2 focus:ring-[#00E676]/20"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] text-slate-300 font-semibold">
              Credit limit
            </label>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              placeholder={`e.g. 5000 (${card?.currency || "GBP"})`}
              data-testid="saltedge-override-limit"
              className="mt-1.5 w-full rounded-lg bg-[#060B19] border border-[#1A2849] px-3 sm:px-3.5 py-2 sm:py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#00E676] focus:ring-2 focus:ring-[#00E676]/20"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] text-slate-300 font-semibold">
              Next due date
            </label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              data-testid="saltedge-override-due"
              className="mt-1.5 w-full rounded-lg bg-[#060B19] border border-[#1A2849] px-3 sm:px-3.5 py-2 sm:py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#00E676] focus:ring-2 focus:ring-[#00E676]/20"
            />
          </div>

          <DialogFooter className="pt-2 flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={saving}
              className="rounded-full border border-white/10 hover:border-white/30 px-5 py-2.5 text-xs text-slate-300 hover:text-white transition-colors"
              data-testid="saltedge-override-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="saltedge-override-save"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#00E676] hover:bg-[#00C863] text-[#060B19] font-semibold px-6 py-2.5 text-xs transition-colors disabled:opacity-60"
            >
              {saving && <Loader2 size={13} className="animate-spin" />}
              {saving ? "Saving…" : "Save"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SaltEdgeOverrideDialog;
